import React, { Fragment, useEffect, useRef } from 'react'
import styled from 'styled-components'
import { getPrimaryColor } from '../../../../style/utils'
import '@splidejs/react-splide/dist/css/splide.min.css'

import { SliderProps, SliderContainerProps } from './Slider.types'
import { Splide, SplideSlide, Options } from '@splidejs/react-splide'

const ARROW_PATH =
  'M13.6875 7.71875L8.6875 12.7188C8.5 12.9062 8.25 13 8 13C7.71875 13 7.46875 12.9062 7.28125 12.7188C6.875 12.3438 6.875 11.6875 7.28125 11.3125L10.5625 8H1C0.4375 8 0 7.5625 0 7C0 6.46875 0.4375 6 1 6H10.5625L7.28125 2.71875C6.875 2.34375 6.875 1.6875 7.28125 1.3125C7.65625 0.90625 8.3125 0.90625 8.6875 1.3125L13.6875 6.3125C14.0938 6.6875 14.0938 7.34375 13.6875 7.71875Z'

const MAIN_SLIDER_OPTIONS: Options = {
  type: 'fade',
  pagination: false,
  arrows: false,
} as const

const THUMBNAIL_SLIDER_OPTIONS: Options = {
  rewind: true,
  fixedWidth: 81,
  fixedHeight: 54,
  isNavigation: true,
  gap: 10,
  focus: 'center',
  pagination: false,
  cover: true,
  arrowPath: ARROW_PATH,
} as const

export const Slider: React.FC<SliderContainerProps> = (props) => {
  const mainSliderRef = useRef(null)
  const thumbnailSliderRef = useRef(null)

  useEffect(() => {
    if (mainSliderRef.current && thumbnailSliderRef.current) {
      // ここでは sync と splide は必ず存在するのでエラーは無視する
      // @ts-ignore
      mainSliderRef.current.sync(thumbnailSliderRef.current.splide)
    }
  }, [mainSliderRef, thumbnailSliderRef])

  return <StyledPage {...props} mainSliderRef={mainSliderRef} thumbnailSliderRef={thumbnailSliderRef} />
}

const Page: React.FC<SliderProps> = ({ className, mainSliderRef, thumbnailSliderRef, images }) => {
  const Slides: React.FC = () => (
    <>
      {images.map((image, i) => {
        if (!image.src) return <Fragment key={i} />
        return (
          <SplideSlide key={image.src}>
            <div className="img-wrapper">
              <img
                src={image.src}
                alt={image.alt || ''}
                onError={(e) => {
                  e.currentTarget.style.display = 'none'
                }}
              />
            </div>
          </SplideSlide>
        )
      })}
    </>
  )

  return (
    <div className={className}>
      <Splide className="main" ref={mainSliderRef} options={MAIN_SLIDER_OPTIONS}>
        <Slides />
      </Splide>
      <Splide className="thumbnail" ref={thumbnailSliderRef} options={THUMBNAIL_SLIDER_OPTIONS}>
        <Slides />
      </Splide>
    </div>
  )
}

const StyledPage = styled(Page)`
  > .main {
    margin-bottom: 8px;

    li > .img-wrapper {
      width: 100%;
      padding-top: 66.66%;
      position: relative;

      > img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        position: absolute;
        top: 0;
        left: 0;
      }
    }
  }

  > .thumbnail {
    li > .img-wrapper {
      width: 100%;
      padding-top: 66.66%;
      position: relative;

      > img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        position: absolute;
        top: 0;
        left: 0;
      }
    }

    .splide__slide {
      &.is-active {
        border: 1px solid ${getPrimaryColor};
      }
    }

    .splide__arrow {
      background-color: rgba(255, 255, 255, 0.8);
      width: 50px;
      height: 50px;
      border-radius: 25px;
      z-index: 1;
      &--prev {
        left: -1.5em;
        top: -14rem;
      }

      &--next {
        right: -1.5em;
        top: -14rem;
      }

      > svg {
        fill: ${getPrimaryColor};

        > path {
          scale: 2.5;
        }
      }
    }
  }

  @media (max-width: 768px) {
    > .thumbnail {
      .splide__arrow {
        &--prev {
          top: 29px;
          left: 0;
        }

        &--next {
          top: 29px;
          right: 0;
        }
      }
    }
  }
`
